import { useCreateStrategy } from "../../api/hooks/useCreateStrategy";
import { useDeleteStrategy } from "../../api/hooks/useDeleteStrategy";
import { useRestoreStrategy } from "../../api/hooks/useRestoreStrategy";
import { useStrategies } from "../../api/hooks/useStrategies";
import { useUpdateStrategy } from "../../api/hooks/useUpdateStrategy";
import type { components } from "../../api/schema";
import type {
  NamedResourceAdapter,
  NormalizedFireAndForget,
  NormalizedMutation,
} from "../named_resource_crud/types";

export type StrategyResponse = components["schemas"]["StrategyResponse"];

function useUpdateStrategyAdapted(): NormalizedMutation<{
  id: string;
  body: { name: string };
}> {
  const m = useUpdateStrategy();
  return {
    mutateAsync: ({ id, body }) => m.mutateAsync({ strategyId: id, body }),
    isPending: m.isPending,
  };
}

function useDeleteStrategyAdapted(): NormalizedMutation<{ id: string }> {
  const m = useDeleteStrategy();
  return {
    mutateAsync: ({ id }) => m.mutateAsync({ strategyId: id }),
    isPending: m.isPending,
  };
}

function useRestoreStrategyAdapted(): NormalizedFireAndForget<{ id: string }> {
  const m = useRestoreStrategy();
  return {
    mutate: ({ id }) => {
      m.mutate({ strategyId: id });
    },
    isPending: m.isPending,
  };
}

export const strategiesAdapter: NamedResourceAdapter<StrategyResponse> = {
  singularLabel: "strategy",
  pluralLabel: "strategies",
  pluralLabelCapitalized: "Strategies",
  pageDescription: "Manage the trading strategies trades are tagged with.",
  emptyMessage: 'No strategies yet. Click "New strategy" to add one.',
  loadErrorMessage: "Failed to load strategies.",
  createDescription:
    "Add a new trading strategy. You can rename or delete it later.",
  editDescription:
    "Rename this strategy. Trades that reference it stay attached.",
  placeholder: "Opening drive",
  tableTestId: "strategies-table",

  useList: useStrategies,
  useCreate: useCreateStrategy,
  useUpdate: useUpdateStrategyAdapted,
  useDelete: useDeleteStrategyAdapted,
  useRestore: useRestoreStrategyAdapted,

  getId: (item) => item.strategy_id,
  isDeleted: (item) => item.is_deleted,
  getName: (item) => item.name,
  getCreatedAt: (item) => item.created_at,
};
